const SITE_URL = "https://www.tier-1.io";
const BUSINESS_NAME = "Tier-1.io";
const CONTACT_EMAIL = "contact@tier-1.io";
const CONTACT_PHONE = ["+1", "909", "720", "8731"].join("");

const areaServed = [
  "Cherry Valley, CA",
  "Beaumont, CA",
  "Calimesa, CA",
  "Redlands, CA",
  "Inland Empire, CA",
  "Riverside County, CA",
  "San Bernardino County, CA",
].map((name) => ({
  "@type": "Place",
  name,
}));

const nationalAreaServed = [{ "@type": "Country", name: "United States" }];

const buildUrl = (path = "/") => {
  if (/^https?:\/\//.test(path)) {
    return path;
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${normalizedPath}`;
};

const normalizeSlug = (value = "") =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const organizationId = `${SITE_URL}/#organization`;
const localBusinessId = `${SITE_URL}/#localbusiness`;

export const buildOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": organizationId,
  name: BUSINESS_NAME,
  url: SITE_URL,
  email: CONTACT_EMAIL,
  telephone: CONTACT_PHONE,
  description:
    "Tier-1.io provides practical AI consulting and workflow automation for small businesses in Cherry Valley, Beaumont, Calimesa, Redlands, and the Inland Empire.",
  areaServed,
  sameAs: ["https://www.tier-1.io"],
});

export const buildLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "ProfessionalService"],
  "@id": localBusinessId,
  name: BUSINESS_NAME,
  url: SITE_URL,
  email: CONTACT_EMAIL,
  telephone: CONTACT_PHONE,
  priceRange: "$$",
  image: `${SITE_URL}/logo512.png`,
  description:
    "Practical AI automation and AI consulting for small businesses in Cherry Valley, Beaumont, Calimesa, Redlands, and the Inland Empire.",
  areaServed,
  parentOrganization: {
    "@id": organizationId,
  },
  knowsAbout: [
    "AI consulting",
    "AI automation",
    "Workflow automation",
    "ChatGPT consulting",
    "Small business automation",
    "Custom AI systems",
  ],
});

export const buildServiceSchema = (service = {}) => {
  const serviceData = typeof service === "string" ? { name: service } : service;
  const name = serviceData.name || "AI Consulting & Workflow Automation";
  const slug = serviceData.slug || normalizeSlug(name);
  const url = serviceData.url || buildUrl(`/${slug}/`);

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    name,
    url,
    description:
      serviceData.description ||
      "Practical AI consulting, implementation, and workflow automation for small businesses in the Inland Empire.",
    provider: {
      "@id": localBusinessId,
    },
    serviceType: serviceData.serviceType || name,
    areaServed: serviceData.areaServed || areaServed,
    audience: serviceData.audience || {
      "@type": "BusinessAudience",
      name: "Small business owners and operators",
    },
    offers: serviceData.offers || {
      "@type": "Offer",
      availability: "https://schema.org/InStock",
      url,
    },
  };
};

export const buildLocationSchema = (location = {}) => {
  const locationData = typeof location === "string" ? { city: location } : location;
  const city = locationData.city || locationData.name || "Inland Empire";
  const region = locationData.region || "CA";
  const slug = locationData.slug || `ai-consulting-${normalizeSlug(city)}-${region.toLowerCase()}`;
  const url = locationData.url || buildUrl(`/${slug}/`);

  return {
    "@context": "https://schema.org",
    "@type": "Place",
    "@id": `${url}#place`,
    name: locationData.name || `${city}, ${region}`,
    url,
    address: {
      "@type": "PostalAddress",
      addressLocality: city,
      addressRegion: region,
      addressCountry: "US",
    },
    containedInPlace: locationData.containedInPlace,
    areaServed,
    subjectOf: {
      "@type": "Service",
      name: locationData.serviceName || `AI Consulting & Automation in ${city}, ${region}`,
      provider: {
        "@id": localBusinessId,
      },
      areaServed: [
        {
          "@type": "Place",
          name: `${city}, ${region}`,
        },
      ],
    },
  };
};

export const buildBreadcrumbSchema = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => {
    const itemData = typeof item === "string" ? { name: item, url: item } : item;

    return {
      "@type": "ListItem",
      position: index + 1,
      name: itemData.name,
      item: buildUrl(itemData.url || itemData.href || "/"),
    };
  }),
});

export const buildFaqSchema = (faqs = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question || faq.name,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer || faq.text,
    },
  })),
});

export { areaServed, nationalAreaServed };
